import { useEffect } from 'react';
import { Hub } from 'aws-amplify/utils';
import useAuthStore from '../store/useAuthStore';
import {
  signUp, confirmSignUp, signIn, signOut, signInWithApple,
  deleteAccount, forgotPassword, confirmForgotPassword,
  getAuthToken, checkAuth,
} from '../services/auth';

// AuthProvider is now a passthrough — Zustand is the source of truth, the side-effect
// orchestration lives in useAuthBootstrap. This shim exists so App.js's <AuthProvider>
// import keeps compiling during the migration; new code should not wrap with it.
export function AuthProvider({ children }) {
  return children;
}

// Bootstrap auth — called ONCE at app root. Wires Cognito Hub events into the store
// and runs the initial session check.
export function useAuthBootstrap() {
  const setConfigured = useAuthStore((s) => s.setConfigured);
  const setLoading = useAuthStore((s) => s.setLoading);
  const clearUser = useAuthStore((s) => s.clearUser);

  useEffect(() => {
    const poolId = process.env.REACT_APP_USER_POOL_ID;
    setConfigured(!!poolId);
    if (poolId) checkAuth();
    else setLoading(false);

    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') checkAuth();
      if (payload.event === 'signedOut') clearUser();
    });
    return () => hubListener();
  }, [setConfigured, setLoading, clearUser]);
}

// Composed hook — same shape as the legacy Context value so existing consumers
// (`const { user, signIn } = useAuth()`) keep working without edits.
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isConfigured = useAuthStore((s) => s.isConfigured);

  return {
    user,
    loading,
    isAuthenticated,
    isConfigured,
    signUp,
    confirmSignUp,
    signIn,
    signInWithApple,
    signOut,
    deleteAccount,
    forgotPassword,
    confirmResetPassword: confirmForgotPassword,
    getAuthToken,
  };
}
